import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS, API_URLS } from "../../../../core/data/redux/apis/urls";
import { formatNatureOfDuty } from "./common/nocUtils";
import { handleDocumentClick } from "./common/documentUtils";
import { NocHeader } from "./common/NocHeader";
import { NocEmployeeCard } from "./common/NocEmployeeCard";
import { NocWorkHistoryCard } from "./common/NocWorkHistoryCard";
import { NocChatSection } from "./common/NocChatSection";
import { NocActionModal } from "./common/NocActionModal";

const formatDate = (dateStr?: string | null) => {
  if (!dateStr || dateStr === "00-00-0000" || dateStr === "0000-00-00" || dateStr === "-") return "-";
  const d = dayjs(dateStr);
  return d.isValid() ? d.format("DD-MM-YYYY") : String(dateStr);
};

interface NocOnlineCourseReimbApprovalProps {
  id: string;
  nocData: any;
  onRefresh: () => void;
  onBack: () => void;
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const NocOnlineCourseReimbApproval: React.FC<NocOnlineCourseReimbApprovalProps> = ({
  id,
  nocData,
  onRefresh,
  onBack,
  onShowToast,
}) => {
  const [approvedAmount, setApprovedAmount] = useState<string>("");
  const [hRemarks, setHRemarks] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [contactNumberError, setContactNumberError] = useState<string>("");
  const [reimbParentCourse, setReimbParentCourse] = useState<any>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  // Initialize approvedAmount
  useEffect(() => {
    if (nocData) {
      setApprovedAmount(
        String(nocData.approvedAmount || nocData.requestAmount || nocData.requestedAmount || "")
      );
      setHRemarks("");
      setContactNumber(
        nocData?.contactNumber ||
        nocData?.employee?.phone ||
        nocData?.employee?.details?.phone ||
        ""
      );
    }
  }, [nocData]);

  // Fetch Parent Online Course if needed
  useEffect(() => {
    let isMounted = true;
    const fetchParentCourse = async () => {
      if (!nocData) return;
      if (nocData.online_course || nocData.noc_online_course) {
        if (isMounted) setReimbParentCourse(nocData.online_course || nocData.noc_online_course);
        return;
      }
      const refNo = nocData.referenceNumber1 || nocData.associatedNocRef;
      if (!refNo) return;

      try {
        let resolved: any = null;
        try {
          const res: any = await Api.post(API_URLS.NOC.FIND_ONE_ONLINE_COURSE, { referenceNumber: refNo });
          const d = res?.data?.data || res?.data;
          if (d && (d.id || d.onlineCourseName)) resolved = d;
        } catch { }

        if (!resolved) {
          try {
            const resAll: any = await Api.post(API_URLS.NOC.FIND_ALL_ONLINE_COURSES, { query: refNo });
            const list = resAll?.data?.data?.data || resAll?.data?.data || resAll?.data;
            if (Array.isArray(list) && list.length > 0) {
              resolved = list.find((c: any) => String(c.referenceNumber).trim() === String(refNo).trim() || String(c.id).trim() === String(refNo).trim()) || list[0];
            }
          } catch { }
        }

        if (isMounted && resolved) {
          setReimbParentCourse(resolved);
        }
      } catch (err) {
        console.warn("Failed fetching parent online course:", err);
      }
    };
    fetchParentCourse();
    return () => { isMounted = false; };
  }, [nocData]);

  const isClaim =
    nocData?.claimOrOnlyAdditi === "CL" ||
    nocData?.reimbursement_type?.type_id === "CL" ||
    nocData?.reimbursement_type?.type_title?.toLowerCase()?.includes("reimburse");

  const requestedAmount = parseFloat(nocData?.requestAmount || nocData?.requestedAmount || "0");

  const handleApproveReject = async (action: "A" | "R") => {
    // 1. Validation for Claim Amount
    if (action === "A" && isClaim) {
      const approved = parseFloat(approvedAmount || "0");
      if (isNaN(approved) || approved <= 0) {
        onShowToast("Error", "Approved amount must be greater than 0.", "danger");
        return;
      }
      if (requestedAmount > 0 && approved > requestedAmount) {
        onShowToast("Error", "Approved amount cannot exceed requested amount.", "danger");
        return;
      }
    }

    // Validation for Contact Number if editable
    const isContactEditable =
      nocData?.enabled_fields_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_fields_json?.contactNumber === "X" ||
      nocData?.enabled_fields_json?.contactNumber === "X";

    if (isContactEditable) {
      if (!contactNumber || !/^\d{10}$/.test(contactNumber)) {
        setContactNumberError("Contact number must be exactly 10 digits");
        onShowToast("Error", "Contact number must be exactly 10 digits.", "danger");
        return;
      }
    }

    // 2. Validation for Approver Level 1
    const isFirstApprover = parseInt(nocData?.approverLevel || nocData?.ApproverLevel || "1") < 2;
    if (action === "A" && isFirstApprover && !(hRemarks || "").trim()) {
      onShowToast("Error", "Remark is required for approval.", "danger");
      return;
    }

    // 3. Validation for Mandatory Remarks on Rejection
    if (action === "R" && !(hRemarks || "").trim()) {
      onShowToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
      const remarksEl = document.getElementById("approver-remarks-textarea");
      if (remarksEl) {
        remarksEl.scrollIntoView({ behavior: "smooth", block: "center" });
        remarksEl.focus();
      }
      return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const payload = {
        Approve_Reject_flag: action,
        referenceNumber: nocData?.referenceNumber,
        hRemarks: (hRemarks || "").trim(),
        EmpID: nocData?.empID,
        approverUserID: nocData?.approver?.empId,
        approvedAmount: approvedAmount || "",
      };

      console.log("Online Course Reimbursement Approval Payload:", payload);
      const res: any = await Api.post(`${BASE_URLS.BASE_URL}noc-reimbursement/approve_reject_action`, payload);

      if (res?.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        onShowToast("Success", `Online Course Reimbursement application ${actionText} successfully`, "success");
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (err: any) {
      console.error("Online Course Reimbursement Approval Error:", err);
      onShowToast("Error", err?.message || "Action failed", "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const courseDetails = nocData?.online_course || nocData?.noc_online_course || reimbParentCourse || {};
  const categoryTitle =
    courseDetails?.category?.category_title ||
    courseDetails?.course_category?.category_title ||
    reimbParentCourse?.onlineCourseCategory ||
    nocData?.onlineCourseCategory ||
    "-";

  const deptTitle =
    courseDetails?.course_category?.course_departments?.department ||
    courseDetails?.onlineCourseDepartment ||
    reimbParentCourse?.onlineCourseDepartment ||
    nocData?.onlineCourseDepartment ||
    "-";

  const courseName =
    courseDetails?.onlineCourseName ||
    courseDetails?.course_category?.course_departments?.course ||
    reimbParentCourse?.onlineCourseName ||
    nocData?.onlineCourseName ||
    "-";

  const platform =
    courseDetails?.course_category?.course_departments?.plateform ||
    courseDetails?.course_category?.course_departments?.platform ||
    reimbParentCourse?.platform ||
    nocData?.platform ||
    "-";

  const rawDuration =
    courseDetails?.course_category?.course_departments?.duration ??
    courseDetails?.durationOfOnlineCourse ??
    reimbParentCourse?.durationOfOnlineCourse ??
    nocData?.durationOfOnlineCourse;
  const durationStr = rawDuration !== undefined && rawDuration !== null ? String(rawDuration).trim() : "";
  const duration = durationStr
    ? durationStr.toLowerCase().includes("week")
      ? durationStr
      : `${durationStr} Weeks`
    : "-";

  const rawFee =
    courseDetails?.course_category?.course_departments?.course_fee ??
    courseDetails?.courseFee ??
    reimbParentCourse?.courseFee ??
    nocData?.courseFee;
  const fee = rawFee !== undefined && rawFee !== null && String(rawFee).trim() !== "" ? String(rawFee).trim() : "-";

  const contributor =
    courseDetails?.course_category?.course_departments?.contributor ||
    reimbParentCourse?.contributor ||
    nocData?.contributor ||
    "-";

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <NocHeader
          title="Application for Reimbursement / Addition of Online Course"
          breadcrumbTitle="Online Course Reimbursement Details"
          nocType="NOC_ONLINECOURSE-REIMB"
          nocData={nocData}
          onBack={onBack}
          onRefresh={onRefresh}
          onShowToast={onShowToast}
        />

        {/* Employee & Application Info */}
        <NocEmployeeCard
          nocData={nocData}
          nocType="NOC_ONLINECOURSE-REIMB"
          editableContactNumber={contactNumber}
          onContactNumberChange={(val) => {
            setContactNumber(val);
            if (/^\d{10}$/.test(val)) setContactNumberError("");
          }}
          contactNumberError={contactNumberError}
        />

        {/* Course Details as per HR Compendium */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-book me-2 text-primary"></i>
              Course Details as per HR Compendium
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-bordered table-striped mb-0 text-center align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Category</th>
                    <th>Department</th>
                    <th>Online Course Name</th>
                    <th>Platform</th>
                    <th>Duration of course</th>
                    <th>Online course fee</th>
                    <th>Contributors</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-medium">{categoryTitle}</td>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-medium">{deptTitle}</td>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-bold text-dark">{courseName}</td>
                    <td style={{ backgroundColor: "#ffeba1" }}>{platform}</td>
                    <td style={{ backgroundColor: "#ffeba1" }}>{duration}</td>
                    <td style={{ backgroundColor: "#ffeba1" }} className="fw-bold text-success">{fee}</td>
                    <td style={{ backgroundColor: "#ffeba1" }}>{contributor}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Reimbursement & Addition Details */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-cash me-2 text-success"></i>
              Reimbursement & Addition Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Request Type
                </label>
                <div className="fw-bold text-dark">
                  {isClaim ? "Reimbursement and Addition of record" : "Only Addition of record in System"}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Block Year Ending On
                </label>
                <div className="fw-bold text-dark">
                  {formatDate(nocData?.endOfBlockDate) !== "-" ? formatDate(nocData?.endOfBlockDate) : "31-03-2027"}
                </div>
              </div>

              {isClaim && (
                <>
                  <div className="col-md-4 col-sm-6">
                    <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                      Amount Remaining for Current Block Year out of (18000)
                    </label>
                    <div className="fw-bold text-primary fs-16">
                      ₹ {nocData?.eligibilityAmount ? Number(nocData.eligibilityAmount).toLocaleString("en-IN") : "18,000"} /-
                    </div>
                  </div>

                  <div className="col-md-4 col-sm-6">
                    <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                      Requested Amount
                    </label>
                    <div className="fw-bold text-success fs-16">
                      ₹ {requestedAmount > 0 ? requestedAmount.toLocaleString("en-IN") : (nocData?.requestAmount ? Number(nocData.requestAmount).toLocaleString("en-IN") : "0.00")} /-
                    </div>
                  </div>

                  <div className="col-md-4 col-sm-6">
                    <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                      Approved Amount <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(e.target.value)}
                      placeholder="Enter approved amount"
                    />
                  </div>

                  <div className="col-12">
                    <div className="p-3 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-bold mb-1">Note</small>
                      <div className="text-dark small">
                        If the fee is in dollars/other currency, please provide the amount in INR.
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Nature of Duty Performing
                </label>
                <div className="fw-bold text-dark">
                  {formatNatureOfDuty(nocData?.natureOfDutyPerfo || nocData?.natureOfDuty, nocData?.nature_of_duty)}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Date of Joining Online Course
                </label>
                <div className="fw-bold text-dark">
                  {formatDate(nocData?.dOJOnlineCourse)}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Date of Completion of Course
                </label>
                <div className="fw-bold text-dark">
                  {formatDate(nocData?.courseCompletionDate)}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Duration of Course
                </label>
                <div className="fw-bold text-dark">
                  {duration !== "-" ? duration : (nocData?.durationOfOnlineCourse ? (String(nocData.durationOfOnlineCourse).toLowerCase().includes("week") ? nocData.durationOfOnlineCourse : `${nocData.durationOfOnlineCourse} Weeks`) : "-")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Previous Reimbursements for Online Courses */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light d-flex align-items-center">
            <i className="ti ti-history me-2 text-primary fs-18"></i>
            <h5 className="card-title mb-0">Previous Reimbursements for Online Courses</h5>
          </div>
          <div className="card-body">
            {Array.isArray(nocData?.previous_reimbursements) && nocData.previous_reimbursements.length > 0 ? (
              <div className="table-responsive border rounded">
                <table className="table table-bordered table-hover align-middle mb-0 text-center">
                  <thead className="table-light">
                    <tr>
                      <th>Category</th>
                      <th>Platform</th>
                      <th>Duration</th>
                      <th>Course Name</th>
                      <th>Date of Joining</th>
                      <th>Completion Date</th>
                      <th>Online Course Fee</th>
                      <th>Reimbursement Date</th>
                      <th>End of Block</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nocData.previous_reimbursements.map((prev: any, idx: number) => (
                      <tr key={idx}>
                        <td>
                          {(typeof prev.category === "object"
                            ? prev.category?.category_title || prev.category?.category_name
                            : prev.category) || "-"}
                        </td>
                        <td>{prev.platform || "-"}</td>
                        <td>{prev.duration || "-"}</td>
                        <td>{prev.courseName || "-"}</td>
                        <td>{formatDate(prev.dateOfJoining)}</td>
                        <td>{formatDate(prev.dateOfCompletion)}</td>
                        <td>{prev.onlineCourseFee ? `₹ ${prev.onlineCourseFee}` : "-"}</td>
                        <td>{formatDate(prev.dateOfReimbursement)}</td>
                        <td>{formatDate(prev.endOfBlock)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>
                <div
                  className="alert text-center fw-bold py-2 mb-3"
                  style={{ backgroundColor: "#ffeba1", color: "#333", border: "1px solid #ffd768" }}
                >
                  No reimbursement taken for Online course previously
                </div>
                <div className="row g-3">
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Category</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Date of Joining Course</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Platform</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Course Duration</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Course Name</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Completion Date</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Online Course Fee</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">Reimbursement Date</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="p-2 bg-light rounded border">
                      <small className="text-muted d-block text-uppercase fw-medium">End of Block</small>
                      <div className="fw-medium text-dark">-</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Attached Documents */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light d-flex align-items-center">
            <i className="ti ti-files me-2 text-primary fs-18"></i>
            <h5 className="card-title mb-0">
              Attached Documents ({Array.isArray(nocData?.documents) ? nocData.documents.length : 0})
            </h5>
          </div>
          <div className="card-body">
            {Array.isArray(nocData?.documents) && nocData.documents.length > 0 ? (
              <div className="table-responsive border rounded">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Document Type</th>
                      <th>File Name</th>
                      <th>Uploaded Date</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nocData.documents.map((doc: any, idx: number) => {
                      const typeLabel =
                        doc.typeOfAttachedDoc === "01"
                          ? "Fee Receipt"
                          : doc.typeOfAttachedDoc === "02"
                          ? "Proof of Payment in INR"
                          : doc.typeOfAttachedDoc === "03"
                          ? "Course Completion Certificate"
                          : doc.documentType || "Attachment";
                      return (
                        <tr key={doc.id || idx}>
                          <td>
                            <span className="badge bg-primary-subtle text-primary border">{typeLabel}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className="ti ti-file-text fs-18 text-primary me-2"></i>
                              <span className="fw-medium">
                                {doc.originalName || (doc.fileName ? doc.fileName.split("/").pop() : `Document ${idx + 1}`)}
                              </span>
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">{formatDate(doc.created_at || nocData?.created_at)}</small>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleDocumentClick(doc, "NOC_ONLINECOURSE-REIMB", onShowToast)}
                            >
                              <i className="ti ti-eye me-1"></i>
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted mb-0">No documents attached.</p>
            )}
          </div>
        </div>

        {/* Notes Card */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-info-circle me-2 text-primary"></i>
              Notes
            </h5>
          </div>
          <div className="card-body">
            <ul className="mb-0 ps-3 text-muted">
              <li>Reimbursement request should be applied within 6 months of completion of the course.</li>
            </ul>
          </div>
        </div>

        {/* Work History */}
        <NocWorkHistoryCard nocData={nocData} />

        {/* Communication & Chat Section */}
        <NocChatSection
          nocData={nocData}
          nocType="NOC_ONLINECOURSE-REIMB"
          onShowToast={onShowToast}
        />

        {/* Actions Modal */}
        <NocActionModal
          hRemarks={hRemarks}
          onHRemarksChange={setHRemarks}
          onApproveReject={handleApproveReject}
          isProcessing={isProcessing}
          processingAction={processingAction}
          showRemarksSection={true}
          remarksRequiredForReject={true}
        />
      </div>
    </div>
  );
};
